import { useEffect, useState } from 'react'
import { api, type InboundMessage } from '../lib/api'

/** Texts that came IN, on the dispatcher's screen.
 *
 *  THIS IS NOT AN ACCEPTANCE QUEUE, AND IT MUST NOT LOOK LIKE ONE
 *
 *  PLAN.html §02 step 5: "A voice 'I've got it' and a card tap are equal ways
 *  to mark the call taken. Response is limited to walkie-talkie or card — not
 *  text or email replies."
 *
 *  So there is no Accept button here, and there never should be. A text is a
 *  claim from a handset: it proves characters were sent, not that a trained
 *  responder read the address and is driving. A voice on the radio is heard by
 *  the dispatcher AND by everyone else on the channel, which is what stops two
 *  members going to the same call.
 *
 *  It exists because the alternative is worse. A member texts "on my way" and
 *  today it lands nowhere at all. Whatever the rule about acceptance, throwing
 *  away something a responder said during an emergency cannot be right. So he
 *  reads it, and logs the acceptance himself — the same click he already makes
 *  when he hears it on the radio.
 *
 *  The strip carries that in words, because a UI that merely omits a button
 *  invites somebody to add one later. */

function ago(when: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(when).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`
}

export function InboundTexts() {
  const [messages, setMessages] = useState<InboundMessage[]>([])
  // Pressing "Seen" used to make a message unreachable from every screen in
  // the product. For a text linked to a call the thread still holds it, but a
  // text from an unknown number left no trace anywhere -- and until the phone
  // book knows a number, unknown is the normal case.
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    let alive = true
    const load = () =>
      api.inboundMessages(!showAll)
        .then((m) => { if (alive) setMessages(m) })
        // Silent on purpose. If inbound texts are not configured, or the
        // endpoint is unreachable, the dispatcher screen must carry on exactly
        // as before — CLAUDE.md: no dependency may block the screen.
        .catch(() => {})
    load()
    const t = setInterval(load, 15_000)
    return () => { alive = false; clearInterval(t) }
  }, [showAll])

  // Nothing unread and not asking for history: the strip costs no space on an
  // ordinary shift.
  if (messages.length === 0 && !showAll) {
    return (
      <div className="inbound-quiet">
        <button type="button" className="linkish" onClick={() => setShowAll(true)}>
          Recent texts
        </button>
      </div>
    )
  }

  async function dismiss(id: string) {
    setMessages((m) => m.filter((x) => x.id !== id))
    try { await api.inboundMarkRead(id) } catch { /* it reappears on the next poll */ }
  }

  return (
    <div className="box inbound-texts">
      <h4>
        Texts in <span className="pill">{messages.length}</span>
        <button type="button" className="linkish" style={{ marginLeft: 'auto' }}
                onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Unread only' : 'Recent'}
        </button>
      </h4>
      <div className="bd">
        {messages.map((m) => (
          <div className="intext" key={m.id}>
            <div className="intext-head">
              <b>
                {m.member_name
                  ? `${m.member_name}${m.callsign ? ` (${m.callsign})` : ''}`
                  : m.contact_name || 'Unknown number'}
              </b>
              <small>
                {ago(m.received_at)}
                {m.incident_number && ` · ${m.incident_number}`}
                {m.media_urls && m.media_urls.length > 0 &&
                  ` · ${m.media_urls.length} attachment(s)`}
              </small>
              {!m.read_at && (
                <button type="button" className="linkish"
                        onClick={() => dismiss(m.id)}>
                  Seen
                </button>
              )}
            </div>
            {m.body && <p className="intext-body">{m.body}</p>}
          </div>
        ))}
      </div>
      <div className="bd" style={{ borderTop: '1px solid var(--line-soft)' }}>
        <p className="footnote" style={{ margin: 0 }}>
          A text is not an acceptance. If somebody is going, log it on the call
          the way you would for a voice on the radio.
        </p>
      </div>
    </div>
  )
}
